/* ============================================================
   Complete The Set - shared quick-add engine (PDP + Cart drawer)

   Extracted from sections/product-complete-set.liquid and rewritten to be
   FULLY EVENT-DELEGATED on `document`, so it survives the cart drawer's
   renderContents() DOM replacement (cards + sheet are rebuilt on every cart
   change; this engine re-queries elements per interaction and never caches).

   Config is read from each `.cts-section` root via data-* attributes:
     data-cts-id          unique suffix (section.id on PDP, 'cart' in drawer)
     data-cts-refresh     'default' (PDP: count bubble + events) | 'drawer'
                          (re-render #CartDrawer via renderContents + cartUpdate)
     data-cts-prop-name/number/patch   line-item property keys for the print
   ============================================================ */
(function () {
  'use strict';
  if (window.__ctsEngineLoaded) return;   // both consumers may load this asset
  window.__ctsEngineLoaded = true;

  var FOCUSABLE = 'button:not(:disabled),input,[tabindex]:not([tabindex="-1"])';
  var bodyScrollY = 0;
  var A = null;   // active sheet state (one open at a time)

  /* ── SCROLL LOCK (iOS-safe) - .body-scroll-lock + --body-scroll-y (base.css) ── */
  function lockBody() {
    bodyScrollY = window.scrollY;
    document.documentElement.style.setProperty('--body-scroll-y', '-' + bodyScrollY + 'px');
    document.body.classList.add('body-scroll-lock');
  }
  function unlockBody() {
    document.body.classList.remove('body-scroll-lock');
    document.documentElement.style.removeProperty('--body-scroll-y');
    window.scrollTo(0, bodyScrollY);
  }

  /* ── FIELD VISIBILITY (verbatim group rules) ── */
  function getVisibleFields(title, productId) {
    var t = title;
    var id = String(productId || '');
    var inc = function (s) { return t.indexOf(s) !== -1; };
    var exc = function (s) { return t.indexOf(s) === -1; };
    /* ONE shared exclusion list, transcribed from snippets/personalization-visibility, so the
       sheet cannot disagree with the page or with the card. 'טי שרט' carries the space the
       catalogue actually uses; this file said 'טיישרט' and the page said 'טישרט', and both
       matched zero products, so neither exclusion had ever fired (2026-09-12). */
    var notExcluded = exc('קדם') && exc('טי שרט') && exc('מכנסיי') &&
                      exc('אימון') && exc('גופיית') && exc('Originals');
    /* A product can be printable with no trigger word in its title. The page has always
       honoured these two ids; this sheet knew nothing about ids, so product 10120133542174
       ("חולצת כדורגל מיוחדת נבחרת ברזיל בצבע זהב", ACTIVE and reachable from the Brazil home
       shirt's complete-the-set list) offered print fields on its page and none here. */
    /* 15999616680222 added 2026-10-20 (Barcelona Golf Wang sky-blue jersey), mirrors personalization-visibility. */
    if (id === '8536669094174' || id === '10120133542174' || id === '15999616680222') {
      return { name: true, number: true, patch: true };   /* Group 0 - id exceptions */
    }
    if (inc('חולצת משחק') && notExcluded) {
      return { name: true, number: true, patch: true };   /* Group 1 - Adults */
    }
    /* Goalkeeper shirts - 2026-09-12. Twelve of the nineteen are titled 'חולצת שוער X' rather
       than 'חולצת משחק שוער X' and so fell out of Group 1, which left the quick-add sheet with
       no name-and-number inputs for a garment whose description promises them. Kids goalkeeper
       sets are not vetoed here: Group 3 below catches them and returns the identical object. */
    if (inc('שוער') && exc('ילדים') && notExcluded) {
      return { name: true, number: true, patch: true };   /* Group 1b - Goalkeepers */
    }
    /* Order is MOST PERMISSIVE FIRST, because the page is not a chain: it ORs four independent
       flags (adult / kids / nba / shorts). Kids grants name+number+patch, a jersey grants
       name+number, shorts grant a number alone - so kids must be tested before both. Before
       2026-09-12 shorts sat above kids here and jerseys sat above kids in both chains, so a
       'מכנסיי ילדים' or 'גופיית ילדים' title would have answered differently in each surface. */
    if (inc('ילדים')) {
      return { name: true, number: true, patch: true };   /* Group 2 - Kids */
    }
    if (inc('גופיית') && exc('אימון')) {
      return { name: true, number: true, patch: false };  /* Group 3 - Jerseys */
    }
    /* Kit word, not 'משחק' - mirrors snippets/personalization-visibility.liquid, where the
       reasoning is written out in full. Short version: every shorts title is 'מכנסיי משחק X',
       so excluding 'משחק' made this branch unreachable for all 56 shorts while their copy
       promised a number. A football shorts title always names its kit and an NBA one never
       does, which is what separates the 11 that get the field from the 16 that do not. This
       function only receives the title, which is why the rule cannot be tag-based here. */
    if (inc('מכנסיי') && exc('אסט דון') && exc('ורדן') &&
        (inc('בית') || inc('חוץ') || inc('שלישית'))) {
      return { name: false, number: true, patch: false }; /* Group 4 - Pants */
    }
    return { name: false, number: false, patch: false };  /* Fallback */
  }

  function applyFieldVisibility(sheet, fields) {
    function toggle(sel, visible) {
      var el = sheet.querySelector(sel);
      if (!el) return;
      el.style.display = visible ? '' : 'none';
      if (!visible) { var i = el.querySelector('input'); if (i) i.value = ''; }
    }
    toggle('.cts-field--name', fields.name);
    toggle('.cts-field--number', fields.number);
    toggle('.cts-field--patch', fields.patch);
  }

  function cfgFromSection(sectionEl) {
    return {
      id:         sectionEl.getAttribute('data-cts-id') || '',
      refresh:    sectionEl.getAttribute('data-cts-refresh') || 'default',
      propName:   sectionEl.getAttribute('data-cts-prop-name')   || 'Player Name',
      propNumber: sectionEl.getAttribute('data-cts-prop-number') || 'Number',
      propPatch:  sectionEl.getAttribute('data-cts-prop-patch')  || 'Patch'
    };
  }

  /* ── OPEN ── */
  function openSheet(card) {
    var sectionEl = card.closest('.cts-section');
    if (!sectionEl) return;
    var cfg     = cfgFromSection(sectionEl);
    var sheet   = document.getElementById('cts-sheet-' + cfg.id);
    var overlay = document.getElementById('cts-overlay-' + cfg.id);
    if (!sheet || !overlay) return;

    var d = card.dataset;
    var variants;
    try { variants = JSON.parse(d.variants); } catch (e) { return; }
    var sizeIdx = d.sizeIdx !== '' ? parseInt(d.sizeIdx, 10) : -1;
    if (isNaN(sizeIdx)) sizeIdx = -1;
    var hasSizes = sizeIdx >= 0;

    var thumbEl = sheet.querySelector('.cts-sheet__thumb');
    var cardImg = card.querySelector('.cts-card__image img');
    if (cardImg && cardImg.src) {
      thumbEl.src = cardImg.src; thumbEl.alt = cardImg.alt || ''; thumbEl.style.display = 'block';
    } else {
      thumbEl.style.display = 'none';
    }
    sheet.querySelector('.cts-sheet__product-title').textContent = d.productTitle || '';
    sheet.querySelector('.cts-sheet__product-price').innerHTML = d.productPrice || '';

    var sizesEl   = sheet.querySelector('.cts-sizes');
    var sizesWrap = sheet.querySelector('.cts-sizes-wrap');
    sizesEl.innerHTML = '';
    if (hasSizes) {
      var seen = Object.create(null);
      variants.forEach(function (v) {
        var sz = v.opts[sizeIdx];
        if (seen[sz]) return;
        seen[sz] = true;
        var ok  = variants.some(function (vv) { return vv.opts[sizeIdx] === sz && vv.ok; });
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cts-size-pill' + (ok ? '' : ' cts-size-pill--oos');
        btn.dataset.size = sz;
        btn.textContent  = sz;
        btn.disabled     = !ok;
        btn.setAttribute('aria-pressed', 'false');
        sizesEl.appendChild(btn);
      });
      sizesWrap.classList.remove('cts-sizes-wrap--hidden');
    } else {
      sizesWrap.classList.add('cts-sizes-wrap--hidden');
    }

    applyFieldVisibility(sheet, getVisibleFields(d.productTitle || '', d.productId || ''));
    sheet.querySelector('.cts-input--name').value   = '';
    sheet.querySelector('.cts-input--number').value = '';
    sheet.querySelector('.cts-input--patch').value  = '';
    var addBtn = sheet.querySelector('.cts-add-btn');
    addBtn.disabled = hasSizes;
    addBtn.classList.remove('cts-add-btn--loading', 'cts-add-btn--success');
    sheet.querySelector('.cts-error--size').classList.remove('cts-error--visible');
    sheet.querySelector('.cts-error--network').classList.remove('cts-error--visible');

    var lastFocusedCta = card.querySelector('.cts-card__cta');
    if (lastFocusedCta) lastFocusedCta.setAttribute('aria-expanded', 'true');

    A = {
      sectionEl: sectionEl, sheet: sheet, overlay: overlay, cfg: cfg,
      variants: variants, sizeIdx: sizeIdx, hasSizes: hasSizes,
      activeCtrl: null, lastFocusedCta: lastFocusedCta
    };

    lockBody();
    overlay.classList.add('cts-overlay--visible'); overlay.setAttribute('aria-hidden', 'false');
    sheet.classList.add('cts-sheet--open');        sheet.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () {
      var c = sheet.querySelector('.cts-sheet__close'); if (c) c.focus();
    });
  }

  /* ── CLOSE ── */
  function closeSheet() {
    if (!A) return;
    if (A.activeCtrl) { A.activeCtrl.abort(); A.activeCtrl = null; }
    unlockBody();
    A.overlay.classList.remove('cts-overlay--visible'); A.overlay.setAttribute('aria-hidden', 'true');
    A.sheet.classList.remove('cts-sheet--open');        A.sheet.setAttribute('aria-hidden', 'true');
    if (A.lastFocusedCta) {
      A.lastFocusedCta.setAttribute('aria-expanded', 'false');
      if (document.contains(A.lastFocusedCta)) A.lastFocusedCta.focus();
    }
    A = null;
  }

  function selectSize(pill) {
    if (!A) return;
    A.sheet.querySelectorAll('.cts-size-pill').forEach(function (p) {
      p.classList.remove('cts-size-pill--active'); p.setAttribute('aria-pressed', 'false');
    });
    pill.classList.add('cts-size-pill--active'); pill.setAttribute('aria-pressed', 'true');
    A.sheet.querySelector('.cts-add-btn').disabled = false;
    A.sheet.querySelector('.cts-error--size').classList.remove('cts-error--visible');
  }

  /* ── ADD TO CART ── */
  function doAddToCart() {
    if (!A) return;
    var sheet = A.sheet, cfg = A.cfg;
    var addBtn   = sheet.querySelector('.cts-add-btn');
    var errSize  = sheet.querySelector('.cts-error--size');
    var errNet   = sheet.querySelector('.cts-error--network');
    var srStatus = sheet.querySelector('[data-cts-sr]');
    var pill     = sheet.querySelector('.cts-size-pill--active');

    if (A.hasSizes && !pill) {
      errSize.classList.add('cts-error--visible');
      srStatus.textContent = errSize.textContent;
      return;
    }

    var variant = null, i;
    if (A.hasSizes && pill) {
      var sz = pill.dataset.size;
      for (i = 0; i < A.variants.length; i++) {
        if (A.variants[i].opts[A.sizeIdx] === sz && A.variants[i].ok) { variant = A.variants[i]; break; }
      }
    } else {
      for (i = 0; i < A.variants.length; i++) {
        if (A.variants[i].ok) { variant = A.variants[i]; break; }
      }
    }
    if (!variant) return;

    var props    = {};
    var nameVal  = sheet.querySelector('.cts-input--name').value.trim();
    var numVal   = sheet.querySelector('.cts-input--number').value.trim();
    var patchVal = sheet.querySelector('.cts-input--patch').value.trim();
    if (nameVal)  props[cfg.propName]   = nameVal;
    if (numVal)   props[cfg.propNumber] = numVal;
    if (patchVal) props[cfg.propPatch]  = patchVal;

    addBtn.classList.add('cts-add-btn--loading');
    addBtn.disabled = true;
    errNet.classList.remove('cts-error--visible');

    A.activeCtrl = new AbortController();

    var body = { id: variant.id, quantity: 1, properties: props };
    if (cfg.refresh === 'drawer') {
      body.sections = ['cart-drawer', 'cart-icon-bubble'];
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify(body),
      signal:  A.activeCtrl.signal
    })
    .then(function (res) { if (!res.ok) throw new Error(res.status); return res.json(); })
    .then(function (data) {
      if (A) A.activeCtrl = null;
      addBtn.classList.remove('cts-add-btn--loading');
      addBtn.classList.add('cts-add-btn--success');
      srStatus.textContent = addBtn.querySelector('.cts-add-btn__success').textContent.trim();

      if (cfg.refresh === 'drawer') {
        /* brief success flash, then rebuild the drawer (which recomputes the
           upsell server-side: drops the just-added item, advances shipping bar) */
        setTimeout(function () {
          unlockBody();
          A = null;
          refreshDrawer(data);
        }, 850);
      } else {
        refreshCart();
        setTimeout(function () {
          closeSheet();
          setTimeout(function () {
            addBtn.classList.remove('cts-add-btn--success');
            addBtn.disabled = false;
          }, 400);
        }, 1600);
      }
    })
    .catch(function (err) {
      if (err.name === 'AbortError') return;
      if (A) A.activeCtrl = null;
      addBtn.classList.remove('cts-add-btn--loading');
      addBtn.disabled = false;
      errNet.classList.add('cts-error--visible');
      srStatus.textContent = errNet.textContent;
    });
  }

  /* ── REFRESH: drawer mode - re-render #CartDrawer + publish cartUpdate ── */
  function refreshDrawer(data) {
    var cd = document.querySelector('cart-drawer');
    if (cd && typeof cd.renderContents === 'function' && data && data.sections) {
      cd.renderContents(data);   // data has .id (variant) + .sections{cart-drawer, cart-icon-bubble}
    }
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'complete-the-set', cartData: data, variantId: data && data.id
        });
      }
    } catch (e) {}
  }

  /* ── REFRESH: default mode (PDP) - count bubble + events ── */
  function refreshCart() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-count-bubble').forEach(function (bubble) {
          var span = bubble.querySelector('span[aria-hidden]');
          if (span) span.textContent = cart.item_count;
        });
        var iconBubble = document.getElementById('cart-icon-bubble');
        if (iconBubble && !iconBubble.querySelector('.cart-count-bubble') && cart.item_count > 0) {
          var div = document.createElement('div');
          div.className = 'cart-count-bubble';
          div.innerHTML =
            '<span aria-hidden="true">' + cart.item_count + '</span>' +
            '<span class="visually-hidden">' + cart.item_count + ' items</span>';
          iconBubble.appendChild(div);
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: cart }));
        document.dispatchEvent(new CustomEvent('theme:cart:update', { bubbles: true, detail: cart }));
      })
      .catch(function () {});
  }

  /* ── GLOBAL DELEGATED LISTENERS (bound once) ── */
  document.addEventListener('click', function (e) {
    var cta = e.target.closest('.cts-card__cta');
    if (cta) { var card = cta.closest('.cts-card'); if (card) openSheet(card); return; }
    if (!A) return;
    if (e.target.closest('.cts-sheet__close')) { closeSheet(); return; }
    if (e.target === A.overlay) { closeSheet(); return; }
    var pill = e.target.closest('.cts-size-pill:not(:disabled)');
    if (pill && A.sheet.contains(pill)) { selectSize(pill); return; }
    var add = e.target.closest('.cts-add-btn');
    if (add && A.sheet.contains(add) && !add.disabled && !add.classList.contains('cts-add-btn--loading')) {
      doAddToCart();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!A || !A.sheet.classList.contains('cts-sheet--open')) return;
    if (e.key === 'Escape') { closeSheet(); return; }
    if (e.key === 'Tab') {
      var els = Array.from(A.sheet.querySelectorAll(FOCUSABLE));
      if (!els.length) { e.preventDefault(); return; }
      var first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  document.addEventListener('input', function (e) {
    var inp = e.target.closest('.cts-input--number');
    if (inp) inp.value = inp.value.replace(/\D/g, '');
  });

  /* BFCache zombie fix (iOS Safari restores position:fixed) */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted && document.body.classList.contains('body-scroll-lock')) unlockBody();
  });

  /* ── AJAX RECOMMENDATIONS FALLBACK (PDP only; cart never emits data-cts-ajax) ── */
  function runAjaxFallbacks(root) {
    (root || document).querySelectorAll('.cts-section[data-cts-ajax="true"]').forEach(function (sectionEl) {
      if (sectionEl.__ctsAjaxDone) return;
      sectionEl.__ctsAjaxDone = true;
      var cfg = cfgFromSection(sectionEl);
      var recsWrapper = document.getElementById('cts-recs-' + cfg.id);
      var gridEl = document.getElementById('cts-grid-' + cfg.id);
      if (!recsWrapper || !gridEl) return;
      fetch(recsWrapper.dataset.url)
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = Array.from(doc.querySelectorAll('.cts-card'));
          if (!cards.length) { sectionEl.style.display = 'none'; return; }
          recsWrapper.remove();
          cards.forEach(function (card) {
            var cta = card.querySelector('.cts-card__cta');
            if (cta) cta.setAttribute('aria-expanded', 'false');
            gridEl.appendChild(card);
          });
          sectionEl.style.opacity = '1';
        })
        .catch(function () { sectionEl.style.display = 'none'; });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { runAjaxFallbacks(); });
  } else {
    runAjaxFallbacks();
  }
  document.addEventListener('shopify:section:load', function (e) { runAjaxFallbacks(e.target); });
})();
